import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { InfoIcon, RotateCcwIcon, TestTubeIcon } from 'lucide-react';
import axios from 'axios';

interface PromptEditorProps {
  projectId: number;
  open: boolean;
  onClose: () => void;
}

export function PromptEditor({ projectId, open, onClose }: PromptEditorProps) {
  const [prompt, setPrompt] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testText, setTestText] = useState('');
  const [testResult, setTestResult] = useState<any>(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (open) {
      loadPrompt();
    }
  }, [open, projectId]);

  const loadPrompt = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/v1/projects/${projectId}/extraction-prompt`);
      setPrompt(response.data.prompt);
      setIsCustom(response.data.is_custom);
    } catch (error) {
      toast.error('Failed to load extraction prompt');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`/api/v1/projects/${projectId}/extraction-prompt`, { prompt });
      toast.success('Extraction prompt updated successfully');
      setIsCustom(true);
      onClose();
    } catch (error) {
      toast.error('Failed to save extraction prompt');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      const response = await axios.post(`/api/v1/projects/${projectId}/extraction-prompt/reset`);
      setPrompt(response.data.prompt);
      setIsCustom(false);
      toast.success('Reset to default prompt');
    } catch (error) {
      toast.error('Failed to reset prompt');
      console.error(error);
    }
  };

  const handleTest = async () => {
    if (!testText.trim()) {
      toast.error('Please enter sample text to test');
      return;
    }

    try {
      setTesting(true);
      const response = await axios.post(`/api/v1/projects/${projectId}/extraction-prompt/test`, {
        prompt,
        sample_text: testText,
        model: 'ollama-7b'
      });
      setTestResult(response.data.extracted_data);
      toast.success('Test completed');
    } catch (error) {
      toast.error('Test failed');
      console.error(error);
    } finally {
      setTesting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Extraction Prompt</DialogTitle>
          <DialogDescription>
            Customize what information is extracted from your documents.
            Add custom fields by following the format in the template.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100" />
          </div>
        ) : (
          <Tabs defaultValue="edit" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="edit">Edit Prompt</TabsTrigger>
              <TabsTrigger value="test">Test Prompt</TabsTrigger>
            </TabsList>

            <TabsContent value="edit" className="space-y-4">
              {isCustom && (
                <Alert>
                  <InfoIcon className="h-4 w-4" />
                  <AlertDescription>
                    You're using a custom extraction prompt for this project.
                    All future documents will be analyzed using this prompt.
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <label className="text-sm font-medium">Extraction Prompt</label>
                <Textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  className="font-mono text-sm min-h-[500px]"
                  placeholder="Enter extraction prompt..."
                />
              </div>

              <div className="bg-muted p-4 rounded-lg space-y-2">
                <p className="text-sm font-medium flex items-center gap-2">
                  <InfoIcon className="h-4 w-4" />
                  Tips for customizing your prompt:
                </p>
                <ul className="text-sm text-muted-foreground space-y-1 ml-6 list-disc">
                  <li>Add fields using format: <code className="bg-background px-1 rounded">**field_name** (type):</code></li>
                  <li>Supported types: text, list, integer, boolean, object</li>
                  <li>Be specific about what to extract for each field</li>
                  <li>LLM will respond in JSON with these exact field names</li>
                  <li>Example custom field: <code className="bg-background px-1 rounded">**allegations** (list): List all allegations made...</code></li>
                </ul>
              </div>
            </TabsContent>

            <TabsContent value="test" className="space-y-4">
              <Alert>
                <TestTubeIcon className="h-4 w-4" />
                <AlertDescription>
                  Test your extraction prompt on sample text to see what will be extracted.
                </AlertDescription>
              </Alert>

              <div className="space-y-2">
                <label className="text-sm font-medium">Sample Document Text</label>
                <Textarea
                  value={testText}
                  onChange={(e) => setTestText(e.target.value)}
                  className="font-mono text-sm min-h-[200px]"
                  placeholder="Paste sample document text here to test extraction..."
                />
              </div>

              <Button onClick={handleTest} className="w-full" disabled={testing}>
                <TestTubeIcon className="h-4 w-4 mr-2" />
                {testing ? 'Testing...' : 'Test Extraction'}
              </Button>

              {testResult && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Extracted Data</label>
                  <div className="bg-muted p-4 rounded-lg">
                    <div className="mb-2 flex flex-wrap gap-2">
                      {Object.keys(testResult).map((field) => (
                        <Badge key={field} variant="secondary">
                          {field}
                        </Badge>
                      ))}
                    </div>
                    <pre className="text-xs overflow-auto max-h-[300px]">
                      {JSON.stringify(testResult, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleReset} disabled={saving || loading}>
            <RotateCcwIcon className="h-4 w-4 mr-2" />
            Reset to Default
          </Button>
          <Button onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Saving...' : 'Save & Apply'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
